import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_ENABLED_CURRENCIES,
  normalizeEnabledCurrencies,
  type Currency,
} from '@/shared/currencies';
import {
  DEFAULT_FORMATS,
  GRID_SETTING_KEYS,
  loadGridSettings,
  migrateStoredFormats,
  saveGridSetting,
  useGridConfig,
  type GridFormats,
} from '@/shared/components/Grid';
import { Card } from '@/shared/components/ui/Card';
import { CurrencyMultiSelect } from '@/shared/components/CurrencyMultiSelect';
import { useI18n } from '@/shared/i18n/useT';
import { useOrg } from '@/shared/org';
import type { Json } from '@/lib/database.types';
import {
  esFormatoFecha,
  formatearExcelConSeparador,
  patronACanonico,
  patronADisplay,
} from '@/shared/formula/excelFormat';

const FORMAT_FIELDS: readonly (keyof GridFormats)[] = [
  'currency',
  'integer',
  'decimal',
  'date',
  'dateWeekday',
  'dateTime',
  'dateTimeWeekday',
  'dayMonth',
  'percent',
  'monthYearLong',
  'monthYearShort',
];

const EJEMPLOS_NUMERO = [39802.39, -39802.39, 0];
const EJEMPLOS_PORCENTAJE = [0.1234, -0.1234, 0];
const EJEMPLO_FECHA = new Date();

function VistaPrevia({
  clave,
  patron,
  separador,
  t,
}: {
  clave: keyof GridFormats;
  patron: string;
  separador: '.' | ',';
  t: ReturnType<typeof useI18n>['t'];
}) {
  const canonico = patronACanonico(patron, separador);
  if (esFormatoFecha(canonico)) {
    const { texto, color } = formatearExcelConSeparador(EJEMPLO_FECHA, canonico, separador);
    return (
      <span className="text-sm text-muted" style={color ? { color } : undefined}>
        {texto}
      </span>
    );
  }
  const ejemplos = clave === 'percent' ? EJEMPLOS_PORCENTAJE : EJEMPLOS_NUMERO;
  return (
    <span className="flex flex-col gap-0.5 text-sm text-muted">
      {ejemplos.map((n) => {
        const { texto, color } = formatearExcelConSeparador(n, canonico, separador);
        return (
          <span key={n} className="flex gap-2">
            <span className="w-16 shrink-0 text-xs text-muted/70">
              {n > 0
                ? t('settings.grid.formats.previewLabels.positive')
                : n < 0
                  ? t('settings.grid.formats.previewLabels.negative')
                  : t('settings.grid.formats.previewLabels.zero')}
            </span>
            <span className="font-mono" style={color ? { color } : undefined}>
              {texto === '' ? t('settings.grid.formats.previewLabels.empty') : texto}
            </span>
          </span>
        );
      })}
    </span>
  );
}

const mapearPatrones =(formatos: GridFormats, f: (patron: string) => string): GridFormats =>
  Object.fromEntries(
    Object.entries(formatos).map(([clave, patron]) => [clave, f(patron as string)])
  ) as unknown as GridFormats;

export function GlobalFormatsSection() {
  const { t } = useI18n();
  const org = useOrg();
  const [formats, setFormats] = useState<GridFormats>(DEFAULT_FORMATS);
  const [separator, setSeparator] = useState<'.' | ','>(',');
  const [currencies, setCurrencies] = useState<readonly Currency[]>(DEFAULT_ENABLED_CURRENCIES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = org.status === 'ready' ? org.orgId : null;
  // Mirrors the `grid_settings_write` policy for an organization-wide row, the
  // same way the alignment section does. See the note there for why `isAdmin`
  // was the wrong shape in both directions.
  const editable = useGridConfig().canEditShared;

  useEffect(() => {
    if (!orgId) {
      setFormats(DEFAULT_FORMATS);
      setSeparator(',');
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    void loadGridSettings(orgId)
      .then((settings) => {
        if (!active) return;
        const storedSeparator = settings[GRID_SETTING_KEYS.decimalSeparator];
        const sep = storedSeparator === '.' || storedSeparator === ',' ? storedSeparator : ',';
        setSeparator(sep);
        setCurrencies(normalizeEnabledCurrencies(settings[GRID_SETTING_KEYS.currencies]));

        const stored = settings[GRID_SETTING_KEYS.globalFormats];
        if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
          const loaded = migrateStoredFormats(stored);
          setFormats(mapearPatrones(loaded, (p) => patronADisplay(p, sep)));
        } else {
          setFormats(mapearPatrones(DEFAULT_FORMATS, (p) => patronADisplay(p, sep)));
        }
      })
      .catch(() => {
        if (active) setError(t('settings.grid.loadError'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orgId, t]);

  const cambiarSeparador = (nuevo: '.' | ',') => {
    // El updater se queda puro: StrictMode lo invoca dos veces y el guardado escribía dos veces.
    setFormats((actuales) => mapearPatrones(actuales, (p) => patronADisplay(patronACanonico(p, separator), nuevo)));
    if (orgId) {
      void saveGridSetting(orgId, GRID_SETTING_KEYS.decimalSeparator, nuevo);
    }
    setSeparator(nuevo);
  };

  const cambiarMonedas = async (next: Currency[]) => {
    setCurrencies(next);
    if (!orgId) return;
    setError(null);
    const saveError = await saveGridSetting(orgId, GRID_SETTING_KEYS.currencies, next);
    if (saveError) setError(t('settings.grid.saveError'));
  };

  const saveFormatos = useCallback(async (formatosAGuardar: GridFormats) => {
    if (!orgId) return;
    setError(null);
    const paraGuardar = mapearPatrones(formatosAGuardar, (p) => patronACanonico(p, separator));
    const formatError = await saveGridSetting(
      orgId,
      GRID_SETTING_KEYS.globalFormats,
      paraGuardar as unknown as Json,
    );
    if (formatError) setError(t('settings.grid.saveError'));
  }, [orgId, separator, t]);

  const restoreDefaults = useCallback(async () => {
    const p = mapearPatrones(DEFAULT_FORMATS, (p) => patronADisplay(p, separator));
    setFormats(p);
    void saveFormatos(p);
  }, [separator, saveFormatos]);

  if (org.status !== 'ready' || loading) return <p className="text-sm text-muted">{t('common.loading')}</p>;

  return (
    <Card title={t('settings.grid.formats.title')} description={t('settings.grid.formats.description')}>
      <p className="mb-5 text-sm text-muted">{t('settings.grid.formats.help')}</p>

      {error && <p role="alert" className="mb-3 rounded-xl bg-danger/10 p-3 text-danger">{error}</p>}

      <div className="mb-3 grid gap-2 rounded-xl border border-border-strong bg-surface p-4 sm:grid-cols-[190px_1fr_210px] sm:items-center">
        <span className="font-bold text-fg">{t('settings.grid.formats.separator')}</span>
        <div className="flex gap-2">
          {(['.', ','] as const).map((opcion) => (
            <button
              key={opcion}
              type="button"
              disabled={!editable}
              onClick={() => cambiarSeparador(opcion)}
              className={`h-10 flex-1 rounded-lg border px-3 font-mono text-sm font-bold transition-colors ${
                separator === opcion
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-border-strong hover:bg-surface-hover disabled:opacity-60'
              }`}
            >
              {opcion === ',' ? '1.234,50' : '1,234.50'}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted/70">
          {t('settings.grid.formats.separatorHint')}
        </span>
      </div>

      <div className="mb-3 grid gap-2 rounded-xl border border-border-strong bg-surface p-4 sm:grid-cols-[190px_1fr_210px] sm:items-center">
        <span className="font-bold text-fg">{t('settings.grid.formats.currencies')}</span>
        <CurrencyMultiSelect value={currencies} disabled={!editable} onChange={(next) => void cambiarMonedas(next)} />
        <span className="text-xs text-muted/70">{t('settings.grid.formats.currenciesHint')}</span>
      </div>

      <div className="space-y-3">
        {FORMAT_FIELDS.map((field) => (
          <label key={field} className="grid gap-2 rounded-xl border border-border-strong bg-surface p-4 sm:grid-cols-[190px_1fr_210px] sm:items-center">
            <span className="font-bold text-fg">{t(`settings.grid.formats.fields.${field}`)}</span>
            <input
              value={formats[field]}
              disabled={!editable}
              onChange={(event) => setFormats((current) => ({ ...current, [field]: event.target.value }))}
              onBlur={(event) => {
                const next = { ...formats, [field]: event.target.value };
                void saveFormatos(next);
              }}
              className="h-10 w-full rounded-lg border border-border-strong bg-bg px-3 font-mono text-sm disabled:opacity-60"
            />
            <VistaPrevia clave={field} patron={formats[field]} separador={separator} t={t} />
          </label>
        ))}
      </div>

      <div className="mt-5 flex gap-2">
        <button
          type="button"
          disabled={!editable}
          onClick={() => void restoreDefaults()}
          className="tap-target rounded-full border border-border-strong bg-surface px-4 text-sm font-medium text-fg hover:bg-surface-hover disabled:opacity-60"
        >
          {t('settings.grid.formats.restoreDefaults')}
        </button>
      </div>

      {!editable && <p className="mt-3 text-sm text-muted">{t('settings.org.readOnly')}</p>}
    </Card>
  );
}
